
  import {
    ref,
    useContext,
    useRoute,
    useRouter,
    watch,
    onMounted,
    onBeforeUnmount,
    nextTick,
  } from '@nuxtjs/composition-api'
  import { io } from 'socket.io-client'
  import Teleport from 'vue2-teleport'
  import { formatRecordDate } from '@/utils/date'
  import { formatPhoneDisplay } from '@/utils/telephony'
  import ToastAlert from '@/components/ToastAlert.vue'
  import CallClientLabel from '@/components/telephony/CallClientLabel.vue'
  import ConfirmModal from '@/components/modals/ConfirmModal.vue'
  import LkAccessOverlay from '@/components/org/LkAccessOverlay.vue'
  import ForcePasswordChangeModal from '@/components/auth/ForcePasswordChangeModal.vue'
  import logger from '@/utils/logger'
  import { socketToastText } from '@/utils/notificationFormatters'
  import { SUPPORT_REPLY_EVENT } from '@/composables/support/useSupportChat'
  import {
    cabinetAppearanceVars,
    setDocumentAppearanceVars,
  } from '@/utils/orgAccent'

  const ORG_CALENDAR_ROUTE_NAME = 'org-orgId-calendar-departmentId'
  const CALL_TOAST_DURATION_MS = 60000

  export default {
    components: {
      ToastAlert,
      CallClientLabel,
      ConfirmModal,
      LkAccessOverlay,
      ForcePasswordChangeModal,
      Teleport,
    },
    middleware: ['auth/access/organization'],
    setup() {
      const { store, env, app, i18n, $config } = useContext()
      const route = useRoute()
      const router = useRouter()

      const socket = ref(null)
      const joinedOrgId = ref(null)
      const toastType = ref('info')
      const toastPosition = ref('bottom-right')
      const toastQueue = ref([])
      const showToastRedirectConfirmModal = ref(false)
      const pendingToastRedirect = ref(null)
      const isToastRedirectInFlight = ref(false)
      const toastBusRef = ref(null)
      const callToastIds = new Map()

      const t = (key, params) => i18n.t(key, params)

      const emitPageRealtime = (event, payload) => {
        try {
          if (typeof window !== 'undefined' && window.$nuxt?.$emit) {
            window.$nuxt.$emit(event, payload)
            return
          }
        } catch (e) {
          logger.error('Ошибка отправки realtime-события через window.$nuxt', e)
        }
        app?.$root?.$emit(event, payload)
      }

      const orgIdFromPayload = (payload) => {
        const routeOrgId = route.value?.params?.orgId
        const payloadOrgId = payload?.orgId

        if (
          payloadOrgId != null &&
          routeOrgId != null &&
          String(payloadOrgId) !== String(routeOrgId)
        ) {
          return null
        }

        return payloadOrgId ?? routeOrgId ?? null
      }

      const connectSocket = () => {
        if (socket.value) return
        const socketUrl = getSocketUrl()
        const token = store.state.auth?.accessToken || ''
        socket.value = io(socketUrl, {
          transports: ['polling', 'websocket'],
          withCredentials: true,
          auth: {
            token,
          },
        })
        socket.value.on('connect', () => {
          const orgId = route.value?.params?.orgId
          if (orgId) joinOrgRoom(orgId)
        })
        socket.value.on('disconnect', () => {})
        socket.value.on('connect_error', (error) => {
          logger.error(
            '[REALTIME] Socket connection error:',
            error?.message || error
          )
        })
        socket.value.on('record:created', (payload) => {
          emitPageRealtime('records-changed', payload)

          if (payload?.source === 'client') {
            const timezone =
              payload?.departmentTimezone || payload?.orgTimezone || null
            const dateLabel = formatRecordRange(
              payload?.start,
              payload?.end,
              timezone
            )
            const dateSuffix = dateLabel
              ? ` ${t('layouts.organization.toasts.on_date')} ${dateLabel}`
              : ''
            const detailsRoute = buildRecordDetailsRoute(payload)
            showToast(
              `${t('layouts.organization.toasts.new_online_record')}${dateSuffix}`,
              toastType.value,
              'bottom-right',
              detailsRoute
                ? {
                    actionText: t('layouts.organization.toasts.view_details'),
                    redirectTo: detailsRoute,
                  }
                : {}
            )
          }
        })

        socket.value.on(SUPPORT_REPLY_EVENT, (payload) => {
          const orgId = orgIdFromPayload(payload)
          if (orgId == null) return

          emitPageRealtime(SUPPORT_REPLY_EVENT, payload)

          if (!$config.supportPageEnabled) return

          const supportPath = `/org/${orgId}/support`
          if (route.value?.path?.replace(/\/$/, '') === supportPath) return

          showToast(
            t('layouts.organization.toasts.support_reply'),
            'info',
            'bottom-right',
            {
              actionText: t('layouts.organization.toasts.open_support'),
              redirectTo: supportPath,
            }
          )
        })

        socket.value.on('call:new', (payload) => {
          const orgId = orgIdFromPayload(payload)
          const sessionId = payload?.sessionId
          if (orgId == null || !sessionId) return

          const phone = String(payload?.fromNumber || '').trim()
          const phoneText =
            formatPhoneDisplay(phone) ||
            t('layouts.organization.toasts.unknown_number')

          const toastId = showToast('', 'info', 'bottom-right', {
            call: { client: payload?.client || null, phoneText },
            actionText: phone ? t('layouts.organization.toasts.book') : '',
            redirectTo: phone
              ? {
                  path: `/org/${orgId}/calendar`,
                  query: payload?.callId
                    ? { phone, callId: String(payload.callId) }
                    : { phone },
                }
              : null,
            skipConfirm: true,
            durationMs: CALL_TOAST_DURATION_MS,
          })
          callToastIds.set(sessionId, toastId)
        })
        socket.value.on('call:end', (payload) => {
          const toastId = callToastIds.get(payload?.sessionId)
          if (!toastId) return
          callToastIds.delete(payload.sessionId)
          hideToast(toastId)
        })

        socket.value.on('posts:updated', (payload) => {
          emitPageRealtime('posts-updated', payload)
        })
        socket.value.on('logo:updated', (payload) => {
          const orgId = orgIdFromPayload(payload)
          if (orgId == null) return

          store.dispatch('org/updateOrgLogo', {
            orgId,
            logo: payload?.logo,
            logoApplyCabinet: payload?.logoApplyCabinet,
          })
        })
        socket.value.on('accent:updated', (payload) => {
          const orgId = orgIdFromPayload(payload)
          if (orgId == null) return

          store.dispatch('org/updateOrgAccent', {
            orgId,
            accentColor: payload?.accentColor,
            accentApplyCabinet: payload?.accentApplyCabinet,
          })
        })
        socket.value.on('main:updated', (payload) => {
          const orgId = orgIdFromPayload(payload)
          if (orgId == null) return

          store.dispatch('org/updateOrgMain', {
            orgId,
            mainColor: payload?.mainColor,
            mainApplyCabinet: payload?.mainApplyCabinet,
          })
        })
        socket.value.on('record:updated', (payload) => {
          emitPageRealtime('records-changed', payload)
          const toast = payload?.toast
          const tz = payload?.timezone || 'Europe/Moscow'
          const currentPath = route.value?.path || ''
          const isActivePostsPage = /\/settings\/activePosts\/?$/.test(
            currentPath
          )
          if (toast === 'cancel') {
            if (isActivePostsPage) return
            const dateLabel = formatRecordRange(
              payload?.start,
              payload?.end,
              tz
            )
            const text = dateLabel
              ? `${t('layouts.organization.toasts.record_cancelled')} (${dateLabel})`
              : t('layouts.organization.toasts.record_cancelled')
            showToast(text, toastType.value, 'bottom-right', {
              durationMs: 3000,
            })
          } else if (toast === 'confirm') {
            if (isActivePostsPage) return
            const dateLabel = formatRecordRange(
              payload?.start,
              payload?.end,
              tz
            )
            const text = dateLabel
              ? `${t('layouts.organization.toasts.record_confirmed')} (${dateLabel})`
              : t('layouts.organization.toasts.record_confirmed')
            showToast(text, toastType.value, 'bottom-right', {
              durationMs: 3000,
            })
          } else if (toast === 'relocate') {
            if (isActivePostsPage) return
            const oldLabel = formatRecordRange(
              payload?.oldStart,
              payload?.oldEnd,
              tz
            )
            const newLabel = formatRecordRange(
              payload?.newStart,
              payload?.newEnd,
              tz
            )
            const text =
              oldLabel && newLabel
                ? `${t('layouts.organization.toasts.record_relocated')}: ${oldLabel} → ${newLabel}`
                : t('layouts.organization.toasts.record_relocated')
            showToast(text, toastType.value, 'bottom-right', {
              durationMs: 3000,
            })
          }
        })
        socket.value.on('record:deleted', (payload) => {
          emitPageRealtime('records-changed', payload)
        })
        socket.value.on('notification:created', (payload) => {
          emitPageRealtime('notification-messages-changed', payload)

          if (payload?.message?.type === 'appointment_created') {
            return
          }

          const detailsRoute = buildNotificationDetailsRoute(payload)
          showToast(
            socketToastText(payload),
            'info',
            'bottom-right',
            detailsRoute
              ? {
                  actionText: t(
                    'layouts.organization.toasts.go_to_notification'
                  ),
                  redirectTo: detailsRoute,
                  requiresConfirm: true,
                }
              : {}
          )
        })
      }

      const getSocketUrl = () => {
        const socketBase = env?.socket_url_dev || ''
        if (socketBase) return socketBase
        if (typeof window !== 'undefined' && window.location?.origin) {
          return window.location.origin
        }
        return ''
      }

      const bindNuxtToast = () => {
        try {
          if (typeof window === 'undefined') return
          const bus = window.$nuxt
          if (!bus?.$on || !bus?.$off) return
          if (toastBusRef.value && toastBusRef.value !== bus) {
            try {
              toastBusRef.value.$off('show-toast', showToast)
            } catch (e) {
              logger.error('Ошибка отвязки обработчика toast-шины', e)
            }
          }
          toastBusRef.value = bus
          bus.$off('show-toast', showToast)
          bus.$on('show-toast', showToast)
        } catch (e) {
          logger.error('Ошибка привязки toast-шины', e)
        }
      }

      onMounted(() => {
        store.dispatch('auth/loadAuthData')
        connectSocket()
        bindNuxtToast()
        nextTick(bindNuxtToast)
        app?.$root?.$on?.('show-toast', showToast)
        app?.$root?.$on?.('ensure-join-org', joinOrgRoom)
        const orgId = route.value?.params?.orgId
        if (orgId && socket.value?.connected) joinOrgRoom(orgId)
      })

      watch(
        () => route.value?.params?.orgId,
        (newOrgId) => {
          if (!socket.value) return
          leaveOrgRoom(joinedOrgId.value)
          joinOrgRoom(newOrgId)
        }
      )

      watch(
        () => route.value.fullPath,
        () => {
          clearTransientToasts()
          bindNuxtToast()
        }
      )

      watch(
        () => store.state.org?.currentOrg,
        (org) => {
          setDocumentAppearanceVars(cabinetAppearanceVars(org))
        },
        { immediate: true, deep: true }
      )

      onBeforeUnmount(() => {
        setDocumentAppearanceVars({})
        try {
          if (typeof window !== 'undefined') {
            toastBusRef.value?.$off?.('show-toast', showToast)
          }
        } catch (e) {
          logger.error('Ошибка отвязки toast-шины при уничтожении layout', e)
        }
        app?.$root?.$off?.('show-toast', showToast)
        app?.$root?.$off?.('ensure-join-org', joinOrgRoom)
        if (socket.value) {
          leaveOrgRoom(joinedOrgId.value)
          socket.value.disconnect()
          socket.value = null
        }
      })

      const formatRecordRange = (startValue, endValue, timezone) => {
        const dateOptions = {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          timeZone: timezone,
        }
        const timeOptions = {
          hour: '2-digit',
          minute: '2-digit',
          timeZone: timezone,
        }

        return formatRecordDate(
          startValue,
          endValue,
          timezone,
          dateOptions,
          timeOptions
        )
      }

      const buildRecordDetailsRoute = (payload) => {
        const orgId = payload?.orgId || route.value.params.orgId
        const departmentId =
          payload?.departmentId || payload?.record?.departmentId
        const recordId = payload?.recordId || payload?.record?.id

        if (!orgId || !departmentId || !recordId) {
          return null
        }

        return {
          path: `/org/${orgId}/calendar/${departmentId}`,
          query: {
            recordId: String(recordId),
          },
        }
      }

      const buildNotificationDetailsRoute = (payload) => {
        const orgId = payload?.orgId || route.value.params.orgId
        const notificationId = payload?.notificationId || payload?.message?.id

        if (!orgId || !notificationId) {
          return null
        }

        return {
          path:
            payload?.url ||
            `/org/${orgId}/settings/notifications/list/${notificationId}`,
        }
      }

      const showToast = (text, type, position, fourth) => {
        const options =
          typeof fourth === 'number'
            ? { durationMs: fourth }
            : fourth && typeof fourth === 'object'
              ? fourth
              : {}
        const resolvedType = type || 'info'
        const resolvedPosition = position || 'bottom-right'
        const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`
        toastQueue.value.push({
          id,
          text,
          actionText: options.actionText || '',
          redirectTo: options.redirectTo || null,
          call: options.call || null,
          requiresConfirm: Boolean(options.requiresConfirm),
          skipConfirm: Boolean(options.skipConfirm),
          type: resolvedType,
          position: resolvedPosition,
        })

        if (typeof options.durationMs === 'number' && options.durationMs > 0) {
          setTimeout(() => {
            hideToast(id)
          }, options.durationMs)
        }

        return id
      }

      const isDepartmentCalendarPage = () => {
        const r = route.value
        if (!r) return false
        if (r.name === ORG_CALENDAR_ROUTE_NAME) return true
        const path = r.path || ''
        return /^\/org\/[^/]+\/calendar(\/[^/]+)?\/?$/.test(path)
      }

      const navigateFromToast = async (toastId, targetRoute) => {
        try {
          await router.push(targetRoute)
        } catch (error) {
          logger.error('Не удалось перейти по маршруту тоста', error)
        } finally {
          hideToast(toastId)
        }
      }

      const handleToastAction = async (toast) => {
        if (isToastRedirectInFlight.value) {
          return
        }

        if (!toast?.redirectTo) {
          hideToast(toast?.id)
          return
        }

        if (
          toast.skipConfirm ||
          (!toast.requiresConfirm && isDepartmentCalendarPage())
        ) {
          await navigateFromToast(toast.id, toast.redirectTo)
          return
        }

        pendingToastRedirect.value = {
          toastId: toast.id,
          targetRoute: toast.redirectTo,
        }
        showToastRedirectConfirmModal.value = true
      }

      const confirmToastRedirect = async () => {
        if (isToastRedirectInFlight.value) {
          return
        }

        const pendingAction = pendingToastRedirect.value
        showToastRedirectConfirmModal.value = false
        pendingToastRedirect.value = null

        if (!pendingAction?.targetRoute) {
          return
        }

        isToastRedirectInFlight.value = true

        try {
          await navigateFromToast(
            pendingAction.toastId,
            pendingAction.targetRoute
          )
        } finally {
          isToastRedirectInFlight.value = false
        }
      }

      const cancelToastRedirect = () => {
        if (isToastRedirectInFlight.value) {
          return
        }

        const toastId = pendingToastRedirect.value?.toastId
        showToastRedirectConfirmModal.value = false
        pendingToastRedirect.value = null

        if (toastId) {
          hideToast(toastId)
        }
      }

      const hideToast = (toastId) => {
        toastQueue.value = toastQueue.value.filter(
          (toast) => toast.id !== toastId
        )

        if (pendingToastRedirect.value?.toastId === toastId) {
          pendingToastRedirect.value = null
          showToastRedirectConfirmModal.value = false
        }
      }

      const clearTransientToasts = () => {
        const removedIds = new Set(
          toastQueue.value
            .filter((toast) => !toast.requiresConfirm)
            .map((toast) => toast.id)
        )

        for (const [sessionId, toastId] of callToastIds) {
          if (removedIds.has(toastId)) {
            callToastIds.delete(sessionId)
          }
        }

        toastQueue.value = toastQueue.value.filter(
          (toast) => toast.requiresConfirm
        )

        if (
          pendingToastRedirect.value &&
          removedIds.has(pendingToastRedirect.value.toastId)
        ) {
          pendingToastRedirect.value = null
          showToastRedirectConfirmModal.value = false
        }
      }

      const joinOrgRoom = (orgId) => {
        if (!orgId || !socket.value) return
        joinedOrgId.value = orgId
        socket.value.emit('join:org', { orgId })
      }

      const leaveOrgRoom = (orgId) => {
        if (!orgId || !socket.value) return
        socket.value.emit('leave:org', { orgId })
      }

      return {
        toastPosition,
        toastQueue,
        showToastRedirectConfirmModal,
        handleToastAction,
        hideToast,
        confirmToastRedirect,
        cancelToastRedirect,
      }
    },
    head: {
      meta: [
        {
          hid: 'robots',
          name: 'robots',
          content: 'noindex, nofollow',
        },
      ],
    },
    computed: {
      mustChangePassword() {
        return this.$store.getters['auth/mustChangePassword']
      },
      isDepartmentCalendarLayout() {
        if (this.$route?.name === ORG_CALENDAR_ROUTE_NAME) {
          return true
        }
        const path = this.$route?.path || ''
        return /^\/org\/[^/]+\/calendar(\/[^/]+)?\/?$/.test(path)
      },
      cabinetAppearanceStyle() {
        return cabinetAppearanceVars(this.$store.state.org?.currentOrg)
      },
    },
  }
